import {
  ArrowDownIcon,
  ArrowUpIcon,
  CaretSortIcon,
  EyeNoneIcon,
} from '@radix-ui/react-icons';
import { type Column } from '@tanstack/react-table';

import { cn } from '~shared/shadcn/lib/utils';
import { Button } from '~shared/shadcn/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '~shared/shadcn/ui/dropdown-menu';

interface DataTableColumnHeaderProps<TData, TValue>
  extends React.HTMLAttributes<HTMLDivElement> {
  column: Column<TData, TValue>;
  title: string;
  disableHiding?: boolean;
  disableSorting?: boolean;
  /** Click the header to cycle asc → desc → none instead of opening the sort menu. */
  sortOnClick?: boolean;
}

export function DataTableColumnHeader<TData, TValue>({
  column,
  title,
  className,
  disableHiding,
  disableSorting,
  sortOnClick,
}: DataTableColumnHeaderProps<TData, TValue>) {
  if (!column.getCanSort() || disableSorting) {
    return (
      <div className={cn('flex text-nowrap text-xs font-bold', className)}>
        {title}
      </div>
    );
  }

  if (sortOnClick) {
    const isSorted = column.getIsSorted();
    return (
      <div className={cn('flex items-center', className)}>
        <Button
          variant='ghost'
          size='sm'
          className='-ml-3 h-8 font-bold focus-visible:ring-gray-200'
          onClick={column.getToggleSortingHandler()}
        >
          <span>{title}</span>
          {isSorted === 'desc' ? (
            <ArrowDownIcon className='ml-2 size-4' />
          ) : isSorted === 'asc' ? (
            <ArrowUpIcon className='ml-2 size-4' />
          ) : (
            <CaretSortIcon className='ml-2 size-4 opacity-50' />
          )}
        </Button>
      </div>
    );
  }

  return (
    <div className={cn('flex w-full items-center space-x-2', className)}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant='ghost'
            size='sm'
            className='-ml-3 h-8 font-bold focus-visible:ring-gray-200 data-[state=open]:bg-accent'
          >
            <span>{title}</span>
            {column.getIsSorted() === 'desc' ? (
              <ArrowDownIcon className='ml-2 size-4' />
            ) : column.getIsSorted() === 'asc' ? (
              <ArrowUpIcon className='ml-2 size-4' />
            ) : (
              <CaretSortIcon className='ml-2 size-4' />
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align='start'>
          <DropdownMenuItem
            onClick={() => {
              column.toggleSorting(false);
            }}
          >
            <ArrowUpIcon className='mr-2 size-3.5 text-black/70' />
            Asc
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => {
              column.toggleSorting(true);
            }}
          >
            <ArrowDownIcon className='mr-2 size-3.5 text-black/70' />
            Desc
          </DropdownMenuItem>
          {disableHiding ? null : (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => {
                  column.toggleVisibility(false);
                }}
              >
                <EyeNoneIcon className='mr-2 size-3.5 text-black/70' />
                Hide
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
